import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type { MouseEvent, ReactNode, Ref } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { ALPHA, BADGE, CONTROL_HEIGHT, FONT_SIZE, GRID_RADIUS, ICON_SIZE, INK, MOTION, NEUTRAL, RADIUS, SURFACE, TOOLBAR_STRIP } from "@/theme/tokens";

/**
 * `standard` lines up with a search field on a 40px toolbar. `micro` is the small strip under a toolbar that holds
 * what is rarely touched (a report's Columns, Summary and Export): `TOOLBAR_STRIP`'s 26px and 12.5px, a notch quieter
 * than the row above.
 */
export type ToolbarTriggerSize = "standard" | "micro";

type TriggerLook = { size: ToolbarTriggerSize; active: boolean; disabled: boolean };

/**
 * The trigger's box: height, border, fill and ink for its size and state. Exported so a control that must sit in the
 * same row but is not this button (a `SystemDropdown` `trigger="button"`, through its `selectSx`) wears the same
 * look instead of a copy of it.
 */
export const toolbarTriggerSx =
	({ size, active, disabled }: TriggerLook) =>
	(theme: Theme): SystemStyleObject<Theme> => {
		const micro = size === "micro";
		const brand = theme.palette.primary;
		return {
			height: micro ? TOOLBAR_STRIP.BUTTON_HEIGHT_PX : CONTROL_HEIGHT.STANDARD,
			minHeight: micro ? TOOLBAR_STRIP.BUTTON_HEIGHT_PX : CONTROL_HEIGHT.STANDARD,
			boxSizing: "border-box",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			gap: 0.75,
			py: 0,
			borderRadius: micro ? GRID_RADIUS.CHIP : `${RADIUS.CARD}px`,
			border: `1px solid ${active ? brand.main : NEUTRAL[300]}`,
			backgroundColor: active ? alpha(brand.main, ALPHA.SELECTED_OVERLAY) : SURFACE,
			color: disabled ? INK.DISABLED : active ? brand.dark : INK.SECONDARY,
			// A button doesn't inherit the page's face on its own; the micro strip's label is set on the button itself.
			...(micro ? { fontFamily: theme.typography.fontFamily } : {}),
			fontSize: micro ? TOOLBAR_STRIP.FONT_SIZE : FONT_SIZE.EMPHASIS,
			fontWeight: active ? 600 : micro ? 400 : 600,
			whiteSpace: "nowrap",
			cursor: disabled ? "default" : "pointer",
			// The standard trigger never animated its hover; only the micro strip's brand-ink hover fades in.
			...(micro ? { transition: `border-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}` } : {}),
			"& svg": { fontSize: micro ? ICON_SIZE.PILL : ICON_SIZE.INLINE },
			...(disabled
				? {}
				: {
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": micro ? { borderColor: brand.main, color: brand.dark } : { backgroundColor: NEUTRAL[50], borderColor: NEUTRAL[400] },
						},
					}),
		};
	};

type ToolbarTriggerButtonProps = {
	/** Already-translated label. Shown unless `iconOnly`. */
	label: string;
	icon: ReactNode;
	/**
	 * Optional trailing count badge — the number of active sort keys, visible columns, and so on.
	 * Omit (or pass `undefined`) for no badge; a badge reading `0` is noise, so 0 is not rendered.
	 */
	count?: number;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
	/** Overrides the tooltip + `aria-label`, which otherwise both come from `label`. */
	description?: string;
	/** Drop the label and render a square icon button (a pill when it also carries a `count`). */
	iconOnly?: boolean;
	/** Omitted it is `standard`, the 40px trigger every toolbar had before `micro`. */
	size?: ToolbarTriggerSize;
	/** Brand edge, wash and ink: what it opens is in effect (a report's summary is on). */
	active?: boolean;
	/** Mirrors whether the panel it opens is showing (`aria-expanded`); given, the tooltip sits above the button. */
	expanded?: boolean;
	/**
	 * `false` drops the tooltip while the label shows and no `description` says more than it, so a labelled trigger
	 * ("Summary · Date · 6 measures") doesn't repeat its own text on hover. An `iconOnly` trigger keeps its tooltip, as
	 * its only visible name.
	 */
	labelTooltip?: boolean;
	dataTestId?: string;
	/** The popover libraries anchor on the real DOM node, so this must reach the button element. */
	ref?: Ref<HTMLButtonElement>;
};

/**
 * The bordered, labelled button that opens a table's toolbar panel — Sort, Columns, and whatever
 * joins them next.
 *
 * Exists because these were each hand-rolled: Sort was a bare `IconButton` wrapping an unlabelled
 * glyph, Columns was a borderless `SystemButton` with text, and side by side they read as two
 * unrelated kinds of control at two different sizes. They do the same thing — open a panel that
 * changes what the table shows — so they share one button, pinned to `CONTROL_HEIGHT.STANDARD` so
 * they also line up with the search field on the same row.
 *
 * An unlabelled sort glyph floating above a grid was a measured defect on the Team page: people did
 * not know it was there, and could not tell it from a filter icon. Hence the word, not just the icon.
 */
export const ToolbarTriggerButton = ({
	label,
	icon,
	count,
	onClick,
	disabled = false,
	description,
	// DEFAULT: labelled — the whole point of the component. Callers opt out for cramped toolbars.
	iconOnly = false,
	// DEFAULT: the 40px trigger, which is what every toolbar had before the micro strip.
	size = "standard",
	// DEFAULT: at rest; only a trigger whose panel is in effect asks for the brand state.
	active = false,
	expanded,
	// DEFAULT: the tooltip repeats the label, as every trigger shipped with it.
	labelTooltip = true,
	dataTestId,
	ref,
}: ToolbarTriggerButtonProps) => {
	const title = description ?? label;
	const showsTooltip = labelTooltip || iconOnly || (description !== undefined && description !== label);
	// A badge is content, so it has to count toward the width: `iconOnly` is a square only while the
	// trigger really is icon-only. Pinned square with one, 44px of content sat in a 40px box and the
	// pill burst through the right border — every multi-key sort on every hand-rolled toolbar.
	const hasCount = count !== undefined && count > 0;
	const square = size === "micro" ? TOOLBAR_STRIP.BUTTON_HEIGHT_PX : CONTROL_HEIGHT.STANDARD;

	return (
		// A panel trigger's tooltip sits above it: its panel opens below, and a tooltip there would cover the panel's heading.
		<InHouseTooltip title={showsTooltip ? title : ""} placement={expanded === undefined ? undefined : "top"}>
			<ButtonBase
				ref={ref}
				onClick={onClick}
				disabled={disabled}
				aria-label={title}
				aria-expanded={expanded}
				data-testid={dataTestId}
				disableRipple
				sx={[
					toolbarTriggerSx({ size, active, disabled }),
					{
						px: iconOnly ? (hasCount ? 0.75 : 0) : size === "micro" ? 1.125 : 1.25,
						width: iconOnly && !hasCount ? square : "auto",
						// Square floor, so no variant comes out narrower than the plain icon one.
						minWidth: square,
						flex: "none",
					},
				]}
			>
				<Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
				{!iconOnly && <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", lineHeight: 1 }}>{label}</Typography>}
				{hasCount && (
					<Box
						sx={{
							minWidth: BADGE.PILL_SIZE,
							height: BADGE.PILL_SIZE,
							px: 0.5,
							borderRadius: BADGE.PILL_RADIUS_FULL,
							bgcolor: disabled ? NEUTRAL[300] : "primary.main",
							color: disabled ? INK.DISABLED : "primary.contrastText",
							fontSize: FONT_SIZE.LABEL_MICRO,
							fontWeight: 700,
							display: "grid",
							placeItems: "center",
						}}
						data-testid={dataTestId ? `${dataTestId}-count` : undefined}
					>
						{count}
					</Box>
				)}
			</ButtonBase>
		</InHouseTooltip>
	);
};
